/** The workshop on Google Maps - fetched only when the visitor asks for it.
 *
 *  A Maps embed is an iframe served by Google, and merely having it in the
 *  page hands Google the visitor's IP address and browser details before they
 *  have done anything but read. So the frame is not in the document until the
 *  button is pressed; what stands there beforehand is our own panel, and
 *  nothing in it contacts Google. This is the „Zwei-Klick-Lösung" the German
 *  supervisory authorities ask for, and the same rule lib/gtag.ts follows for
 *  Google Analytics - with the difference that here the click *is* the
 *  request, so the map needs no banner of its own and no stored consent: it
 *  loads for this one view, because this one visitor just asked for it.
 *
 *  „Route planen" is a plain outbound link and is therefore always shown. It
 *  takes the visitor to Google; it does not bring Google into this page.
 *
 *  The „Google Maps" section of the Datenschutzerklärung
 *  (backend/seed/privacy_sections.py) describes exactly this behaviour - when
 *  what happens here changes, that text has to change with it.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { Locale } from '../lib/api'
import { useLocale } from '../lib/hooks'
import { fill, pathFor, t } from '../lib/i18n'
import { PlainButton } from './ui'

/** The keyless embed: Google looks the address up itself, so the site needs
 *  no API key and no stored coordinates. Valid only inside an iframe - opened
 *  as a page it answers „must be used in an iframe". */
function embedSrc(query: string, locale: Locale): string {
  const params = new URLSearchParams({ q: query, z: '16', hl: locale, output: 'embed' })
  return `https://www.google.com/maps?${params}`
}

/** Where „Route planen" goes: Google's own directions form, in a new tab. */
function directionsHref(query: string): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(query)}`
}

export default function LocationMap({
  query,
  className = '',
}: {
  query: string
  className?: string
}) {
  const locale = useLocale()
  const strings = t(locale).map
  const [loaded, setLoaded] = useState(false)

  return (
    <div className={className} data-testid="location-map">
      <h2 className="m-0 mb-5 font-serif text-[32px] font-medium leading-[1.15] text-ink">
        {strings.title}
      </h2>

      <div className="h-[420px] overflow-hidden rounded-[2px] border border-line max-md:h-[300px]">
        {loaded ? (
          <iframe
            src={embedSrc(query, locale)}
            title={fill(strings.frameTitle, { address: query })}
            width="100%"
            height="100%"
            loading="lazy"
            // Google resolves the address from the URL alone; it has no need
            // for the page the visitor came from.
            referrerPolicy="no-referrer"
            className="block border-0"
            data-testid="location-map-frame"
          />
        ) : (
          <div
            className="flex h-full flex-col items-center justify-center gap-5 bg-paper-deep px-6 text-center"
            data-testid="location-map-placeholder"
          >
            <p className="m-0 font-serif text-[22px] font-medium leading-[1.4] text-ink">
              {query}
            </p>
            <PlainButton
              type="button"
              variant="primary"
              onClick={() => setLoaded(true)}
              data-testid="location-map-load"
            >
              {strings.load}
            </PlainButton>
            {/* Same floor as the consent bar: 12px in the slate tone, which
                clears WCAG AA. A notice nobody can read is not a notice. */}
            <p className="m-0 max-w-[52ch] font-sans text-[13px] leading-[1.5] text-slate">
              {strings.note}{' '}
              <Link
                to={pathFor(locale, 'datenschutz')}
                className="underline hover:no-underline"
                data-testid="location-map-privacy-link"
              >
                {strings.more}
              </Link>
            </p>
          </div>
        )}
      </div>

      <p className="m-0 mt-4 font-sans text-[16px] font-semibold leading-[1.6] text-ink">
        <a
          href={directionsHref(query)}
          target="_blank"
          rel="noreferrer"
          className="underline hover:no-underline"
          data-testid="location-map-directions"
        >
          {strings.directions}
        </a>
      </p>
    </div>
  )
}
